import type { QuizSourceConfig } from './quizSource';
import { quizEditionHasPlayableItems, type QuizEditionSummary } from './types';
import { getCachedFormatter } from '@/utils/dateUtils';

export type RefreshPolicyMode = 'none' | 'manual' | 'feed_driven' | 'scheduled';
export type RefreshPolicyRecurrenceIntent = 'one_time' | 'recurring';
export type RefreshPolicyPublishingMode = 'automatic' | 'review';
export type UpcomingQuizMilestoneKind = 'draft' | 'go_live' | 'awaiting_approval';

export interface RefreshPolicy {
  mode: RefreshPolicyMode;
  /**
   * Durable one-time/recurring choice. Optional only while legacy in-memory fixtures and
   * pre-migration rows pass through this interface; parseRefreshPolicy always resolves it.
   */
  recurrenceIntent?: RefreshPolicyRecurrenceIntent;
  /** False only while first-time setup still needs an explicit one-time/recurring choice. */
  recurrenceChoiceConfirmed?: boolean;
  /** False only while recurring setup still needs an explicit publishing-mode choice. */
  publishingChoiceConfirmed?: boolean;
  /** When true, generated editions require manual approval before publish. Defaults on. */
  reviewRequired: boolean;
  /** Hours before the scheduled publish cadence when the next draft should be prepared. */
  prepLeadHours?: number;
  feedUrl?: string;
  scheduleDays?: number[];
  scheduleTime?: string;
  scheduleTimezone?: string;
}

export const DEFAULT_REFRESH_POLICY: RefreshPolicy = {
  mode: 'manual',
  recurrenceIntent: 'one_time',
  reviewRequired: true,
};

/** Default policy applied when connecting an RSS feed. */
export const DEFAULT_RSS_REFRESH_POLICY: RefreshPolicy = {
  mode: 'feed_driven',
  recurrenceIntent: 'recurring',
  reviewRequired: true,
};

const DEFAULT_PREP_LEAD_HOURS = 12;
const DEFAULT_SCHEDULE_DAYS = [0, 1, 2, 3, 4, 5, 6];
const DEFAULT_SCHEDULE_TIME = '09:00';
const DEFAULT_SCHEDULE_TIMEZONE = 'UTC';
const HOUR_MS = 60 * 60 * 1000;

const REFRESH_POLICY_MODES = new Set<RefreshPolicyMode>([
  'none',
  'manual',
  'feed_driven',
  'scheduled',
]);

export function resolveRefreshPolicyRecurrenceIntent(
  policy: Pick<RefreshPolicy, 'mode' | 'recurrenceIntent'>
): RefreshPolicyRecurrenceIntent {
  if (policy.recurrenceIntent === 'one_time' || policy.recurrenceIntent === 'recurring') {
    return policy.recurrenceIntent;
  }
  return policy.mode === 'feed_driven' || policy.mode === 'scheduled' ? 'recurring' : 'one_time';
}

/**
 * True when the Quiz uses product-facing Upcoming/Live edition workflow.
 * This is intentionally distinct from whether a recurring cadence is fully configured.
 */
export function refreshPolicyUsesRecurringEditionWorkflow(
  policy: Pick<RefreshPolicy, 'mode' | 'recurrenceIntent'>
): boolean {
  return resolveRefreshPolicyRecurrenceIntent(policy) === 'recurring';
}

/** True only for the persisted first-time setup state that still needs a publishing choice. */
export function refreshPolicyNeedsPublishingChoice(
  policy: Pick<RefreshPolicy, 'mode' | 'recurrenceIntent' | 'publishingChoiceConfirmed'>
): boolean {
  return (
    refreshPolicyUsesRecurringEditionWorkflow(policy) && policy.publishingChoiceConfirmed === false
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseScheduleDays(value: unknown): number[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const days = value
    .map((day) => (typeof day === 'number' ? day : Number(day)))
    .filter((day) => Number.isInteger(day));

  return days.length > 0 ? days : undefined;
}

function parsePrepLeadHours(value: unknown): number | undefined {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) {
    return undefined;
  }

  const hours = Math.trunc(parsed);
  return hours >= 0 && hours <= 168 ? hours : undefined;
}

export function parseRefreshPolicy(value: unknown): RefreshPolicy {
  if (!isRecord(value)) {
    return DEFAULT_REFRESH_POLICY;
  }

  const mode = value.mode;
  const parsedMode =
    typeof mode === 'string' && REFRESH_POLICY_MODES.has(mode as RefreshPolicyMode)
      ? (mode as RefreshPolicyMode)
      : DEFAULT_REFRESH_POLICY.mode;
  const reviewRequired = value.reviewRequired;
  const feedUrl = value.feedUrl;
  const scheduleTime = value.scheduleTime;
  const scheduleTimezone = value.scheduleTimezone;
  const prepLeadHours = parsePrepLeadHours(value.prepLeadHours);
  const explicitRecurrenceIntent =
    value.recurrenceIntent === 'one_time' || value.recurrenceIntent === 'recurring'
      ? value.recurrenceIntent
      : null;

  const policy: RefreshPolicy = {
    mode: parsedMode,
    recurrenceIntent: resolveRefreshPolicyRecurrenceIntent({
      mode: parsedMode,
      ...(explicitRecurrenceIntent ? { recurrenceIntent: explicitRecurrenceIntent } : {}),
    }),
    reviewRequired: typeof reviewRequired === 'boolean' ? reviewRequired : true,
  };

  if (typeof value.recurrenceChoiceConfirmed === 'boolean') {
    policy.recurrenceChoiceConfirmed = value.recurrenceChoiceConfirmed;
  }

  if (typeof value.publishingChoiceConfirmed === 'boolean') {
    policy.publishingChoiceConfirmed = value.publishingChoiceConfirmed;
  }

  if (typeof feedUrl === 'string' && feedUrl.length > 0) {
    policy.feedUrl = feedUrl;
  }

  if (prepLeadHours !== undefined) {
    policy.prepLeadHours = prepLeadHours;
  }

  const scheduleDays = parseScheduleDays(value.scheduleDays);
  if (scheduleDays) {
    policy.scheduleDays = scheduleDays;
  }

  if (typeof scheduleTime === 'string' && scheduleTime.length > 0) {
    policy.scheduleTime = scheduleTime;
  }

  if (typeof scheduleTimezone === 'string' && scheduleTimezone.length > 0) {
    policy.scheduleTimezone = scheduleTimezone;
  }

  return policy;
}

/** True when refresh policy stores an active or configured recurring cadence. */
export function refreshPolicyHasSchedule(policy: RefreshPolicy): boolean {
  if (policy.recurrenceChoiceConfirmed === false) {
    return false;
  }
  return (
    policy.mode === 'feed_driven' ||
    policy.mode === 'scheduled' ||
    Boolean(policy.scheduleDays?.length || policy.scheduleTime || policy.scheduleTimezone)
  );
}

export type RefreshPolicyFeedStatus = 'disconnected' | 'paused' | 'active';
export type RefreshPolicyApprovalState = 'none' | 'preparing_draft' | 'awaiting_approval';

export interface RefreshPolicyWorkspaceState {
  refreshPolicy: RefreshPolicy;
  sourceConfig: QuizSourceConfig | null;
  draftEdition: QuizEditionSummary | null;
}

export interface ResolvedRefreshPolicyState {
  hasSchedule: boolean;
  feedUrl: string | null;
  feedStatus: RefreshPolicyFeedStatus;
  hasRecurringIntent: boolean;
  hasRecurringSchedule: boolean;
  isScheduleDeferred: boolean;
  approvalState: RefreshPolicyApprovalState;
  nextPublishAt: Date | null;
  nextDraftAt: Date | null;
  draftEditionScheduledAt: Date | null;
  scheduledDraftGenerationAt: Date | null;
  publishCountdownAt: Date | null;
  hasPublishCountdown: boolean;
  hasDraftCountdown: boolean;
  hasScheduledDraftGeneration: boolean;
}

export interface ResolveRefreshPolicyStateOptions {
  approvalReady?: boolean;
  now?: Date;
}

export function resolveRefreshPolicyState(
  workspace: RefreshPolicyWorkspaceState,
  options: ResolveRefreshPolicyStateOptions = {}
): ResolvedRefreshPolicyState {
  const feedUrl =
    workspace.refreshPolicy.feedUrl ??
    (workspace.sourceConfig?.kind === 'rss_feed'
      ? (workspace.sourceConfig.input.feed_url ?? null)
      : null);
  const needsPublishingChoice = refreshPolicyNeedsPublishingChoice(workspace.refreshPolicy);
  const feedStatus: RefreshPolicyFeedStatus =
    workspace.refreshPolicy.mode === 'feed_driven' && !needsPublishingChoice
      ? 'active'
      : feedUrl
        ? 'paused'
        : 'disconnected';
  const hasSchedule = refreshPolicyHasSchedule(workspace.refreshPolicy) || feedUrl !== null;
  const hasRecurringSchedule =
    !needsPublishingChoice &&
    (feedStatus === 'active' || workspace.refreshPolicy.mode === 'scheduled');
  const requiresDraftApproval =
    hasSchedule &&
    !needsPublishingChoice &&
    workspace.refreshPolicy.reviewRequired &&
    workspace.draftEdition?.approvalStatus === 'unapproved';
  const approvalState: RefreshPolicyApprovalState = !requiresDraftApproval
    ? 'none'
    : options.approvalReady === false
      ? 'preparing_draft'
      : 'awaiting_approval';
  const now = options.now ?? new Date();
  const hasRecurringIntent = refreshPolicyUsesRecurringEditionWorkflow(workspace.refreshPolicy);
  const isScheduleDeferred = hasRecurringIntent && !hasRecurringSchedule;
  const draftEdition = workspace.draftEdition;
  const draftEditionScheduledAt = parseDate(draftEdition?.scheduledFor);
  const draftCanClaimSchedule = Boolean(
    draftEdition &&
    !draftEdition.cancelledAt &&
    (draftEdition.approvalStatus === 'approved' ||
      (!workspace.refreshPolicy.reviewRequired && quizEditionHasPlayableItems(draftEdition)))
  );
  const draftPublishAt =
    draftEdition && draftCanClaimSchedule ? parseFutureDate(draftEdition.scheduledFor, now) : null;
  const nextPublishAt = hasRecurringSchedule
    ? getNextScheduledPublishAt(workspace.refreshPolicy, now)
    : null;
  const publishCountdownAt =
    draftPublishAt ??
    (draftCanClaimSchedule && draftEditionScheduledAt === null ? nextPublishAt : null);
  const nextDraftAt = hasRecurringSchedule
    ? getNextScheduledDraftAt(workspace.refreshPolicy, now, publishCountdownAt)
    : null;
  const scheduledDraftGenerationAt = nextDraftAt;
  const hasPublishCountdown = !requiresDraftApproval && publishCountdownAt !== null;
  const hasDraftCountdown =
    !requiresDraftApproval &&
    (draftPublishAt !== null ||
      hasRecurringSchedule ||
      parseFutureDate(workspace.draftEdition?.scheduledFor, now) !== null);
  const hasScheduledDraftGeneration = hasRecurringSchedule;

  return {
    hasSchedule,
    feedUrl,
    feedStatus,
    hasRecurringIntent,
    hasRecurringSchedule,
    isScheduleDeferred,
    approvalState,
    nextPublishAt,
    nextDraftAt,
    draftEditionScheduledAt,
    scheduledDraftGenerationAt,
    publishCountdownAt,
    hasPublishCountdown,
    hasDraftCountdown,
    hasScheduledDraftGeneration,
  };
}

function getNextScheduledDraftAt(
  policy: RefreshPolicy,
  now: Date,
  currentDraftPublishAt: Date | null
): Date | null {
  const leadHours = policy.prepLeadHours ?? DEFAULT_PREP_LEAD_HOURS;
  // Keep an elapsed preparation window due until its draft claims the publish slot.
  // Advancing by the lead time would skip that slot while the scheduler catches up.
  const publishAfter =
    currentDraftPublishAt && currentDraftPublishAt.getTime() > now.getTime()
      ? currentDraftPublishAt
      : now;
  const publishAt = getNextScheduledPublishAt(policy, publishAfter);
  return publishAt ? new Date(publishAt.getTime() - leadHours * HOUR_MS) : null;
}

function getNextScheduledPublishAt(policy: RefreshPolicy, after: Date): Date | null {
  const scheduleDays = policy.scheduleDays?.length ? policy.scheduleDays : DEFAULT_SCHEDULE_DAYS;
  const [hour, minute] = parseScheduleTime(policy.scheduleTime ?? DEFAULT_SCHEDULE_TIME);
  const timezone = policy.scheduleTimezone ?? DEFAULT_SCHEDULE_TIMEZONE;
  const afterParts = readZonedParts(after, timezone);
  if (!afterParts) return null;

  const year = Number.parseInt(afterParts.year ?? '', 10);
  const month = Number.parseInt(afterParts.month ?? '', 10);
  const day = Number.parseInt(afterParts.day ?? '', 10);
  if ([year, month, day].some((part) => Number.isNaN(part))) return null;

  for (let offset = 0; offset <= 370; offset += 1) {
    const localDate = new Date(Date.UTC(year, month - 1, day + offset));
    if (!scheduleDays.includes(localDate.getUTCDay())) continue;

    const candidate = zonedDateTimeToUtc(
      localDate.getUTCFullYear(),
      localDate.getUTCMonth() + 1,
      localDate.getUTCDate(),
      hour,
      minute,
      timezone
    );
    if (candidate && candidate.getTime() > after.getTime()) return candidate;
  }

  return null;
}

function parseScheduleTime(time: string): [number, number] {
  const [hourRaw, minuteRaw = '00'] = time.split(':');
  const hour = Number.parseInt(hourRaw, 10);
  const minute = Number.parseInt(minuteRaw, 10);
  if (Number.isNaN(hour) || hour < 0 || hour > 23 || Number.isNaN(minute)) {
    return [9, 0];
  }
  return [hour, Math.min(Math.max(minute, 0), 59)];
}

function readZonedParts(date: Date, timeZone: string): Record<string, string> | null {
  try {
    const parts = getCachedFormatter({
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(date);
    return Object.fromEntries(parts.map((part) => [part.type, part.value]));
  } catch {
    return null;
  }
}

function getTimeZoneOffsetMs(date: Date, timeZone: string): number | null {
  const parts = readZonedParts(date, timeZone);
  if (!parts) return null;

  const values = ['year', 'month', 'day', 'hour', 'minute', 'second'].map((part) =>
    Number.parseInt(parts[part] ?? '', 10)
  );
  if (values.some((part) => Number.isNaN(part))) return null;
  const [year, month, day, hour, minute, second] = values;
  return Date.UTC(year, month - 1, day, hour, minute, second) - date.getTime();
}

function zonedDateTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string
): Date | null {
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
  const offsetMs = getTimeZoneOffsetMs(utcGuess, timeZone);
  if (offsetMs === null) return null;
  const candidate = new Date(utcGuess.getTime() - offsetMs);
  // A DST transition can fall between the UTC guess and the actual local time.
  const candidateOffsetMs = getTimeZoneOffsetMs(candidate, timeZone);
  if (candidateOffsetMs !== null && candidateOffsetMs !== offsetMs) {
    const corrected = new Date(utcGuess.getTime() - candidateOffsetMs);
    if (getTimeZoneOffsetMs(corrected, timeZone) === candidateOffsetMs) return corrected;
  }
  return candidate;
}

function parseFutureDate(value: string | null | undefined, now: Date): Date | null {
  const parsed = parseDate(value);
  return parsed && parsed.getTime() > now.getTime() ? parsed : null;
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}
